import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MaterialModule } from 'src/app/material.module';
import { DashboardService } from 'src/app/core/services/dashboard/dashboard.service';
import { ToastService } from 'src/app/shared/services/toast.service';

interface QuickAction {
  title: string;
  subtitle: string;
  route: string;
  icon: string;
  gradientClass: string;
  badgeText: string;
}

interface ChartItem {
  label: string;
  count: number;
  [key: string]: any;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [MaterialModule, CommonModule, RouterModule],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {
  // Stats
  totalUsers: number = 0;
  totalProfiles: number = 0;
  totalVideos: number = 0;
  statsLoading: boolean = false;
  chartLoading: boolean = false;
  downloading: boolean = false;
  isRefreshing: boolean = false;

  // Chart data
  chartData: ChartItem[] = [];
  selectedFilter: string = '7days';
  filters = [
    { value: '7days', label: 'Last 7 Days', shortLabel: '7D' },
    { value: '4weeks', label: 'Last 4 Weeks', shortLabel: '4W' },
    { value: '12months', label: 'Last 12 Months', shortLabel: '12M' }
  ];
  maxCount: number = 0;

  // Computed summary metrics for active period
  totalSessionsInPeriod: number = 0;
  avgSessionsInPeriod: number = 0;
  peakSessionCount: number = 0;
  peakSessionLabel: string = '';
  activeHoverItem: ChartItem | null = null;

  // Quick Action Shortcuts
  quickActions: QuickAction[] = [
    {
      title: 'User Management',
      subtitle: 'Manage athlete accounts & profiles',
      route: '/admin/user-list',
      icon: 'people',
      gradientClass: 'action-users',
      badgeText: 'Active'
    },
    {
      title: 'Top Leaderboard',
      subtitle: 'Analyze punch records & top scores',
      route: '/admin/leaderboard-list',
      icon: 'emoji_events',
      gradientClass: 'action-leaderboard',
      badgeText: 'Rankings'
    },
    {
      title: 'Video Recordings',
      subtitle: 'Review session workout clips',
      route: '/admin/user-recordings',
      icon: 'videocam',
      gradientClass: 'action-recordings',
      badgeText: 'Media'
    },
    {
      title: 'Training Tutorials',
      subtitle: 'Curate drills and coaching content',
      route: '/admin/tutorial',
      icon: 'school',
      gradientClass: 'action-tutorials',
      badgeText: 'Guides'
    }
  ];

  constructor(
    private dashboardService: DashboardService,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    this.loadAllData();
  }

  /**
   * Load both stats and chart data simultaneously
   */
  loadAllData(): void {
    this.loadDashboardStats();
    this.loadChartData();
  }

  /**
   * Manual refresh trigger
   */
  refreshDashboard(): void {
    this.isRefreshing = true;
    this.loadDashboardStats();
    this.loadChartData(() => {
      this.isRefreshing = false;
      this.toastService.successToast('Dashboard data refreshed');
    });
  }

  /**
   * Load dashboard top-level statistics
   */
  loadDashboardStats(): void {
    this.statsLoading = true;
    this.dashboardService.getDashboardStats().subscribe({
      next: (res: any) => {
        this.statsLoading = false;
        if (res.isSuccess && res.item) {
          this.totalUsers = res.item.totalUsers || 0;
          this.totalProfiles = res.item.totalProfiles || 0;
          this.totalVideos = res.item.totalVideos || 0;
        }
      },
      error: (error: any) => {
        this.statsLoading = false;
        console.error('Error loading dashboard stats:', error);
      }
    });
  }

  /**
   * Load chart session data based on selected filter
   */
  loadChartData(callback?: () => void): void {
    this.chartLoading = true;
    this.dashboardService.getVideoSessionCounts(this.selectedFilter).subscribe({
      next: (res: any) => {
        this.chartLoading = false;
        if (res.isSuccess && res.item) {
          this.chartData = Array.isArray(res.item) ? res.item : [];
          this.calculateChartMetrics();
        } else {
          this.chartData = [];
          this.resetMetrics();
        }
        if (callback) callback();
      },
      error: (error: any) => {
        this.chartLoading = false;
        console.error('Error loading chart data:', error);
        this.chartData = [];
        this.resetMetrics();
        if (callback) callback();
      }
    });
  }

  /**
   * Compute aggregated metrics for the active filter period
   */
  calculateChartMetrics(): void {
    if (!this.chartData || this.chartData.length === 0) {
      this.resetMetrics();
      return;
    }

    let sum = 0;
    let max = 0;
    let peakLabel = '';

    for (const item of this.chartData) {
      const count = Number(item.count) || 0;
      sum += count;
      if (count >= max) {
        max = count;
        peakLabel = item.label;
      }
    }

    this.maxCount = max > 0 ? max : 1;
    this.totalSessionsInPeriod = sum;
    this.avgSessionsInPeriod = this.chartData.length > 0 
      ? Math.round((sum / this.chartData.length) * 10) / 10 
      : 0;
    this.peakSessionCount = max;
    this.peakSessionLabel = peakLabel || 'N/A';
  }

  private resetMetrics(): void {
    this.maxCount = 1;
    this.totalSessionsInPeriod = 0;
    this.avgSessionsInPeriod = 0;
    this.peakSessionCount = 0;
    this.peakSessionLabel = 'N/A';
  }

  /**
   * Handle filter switch
   */
  onFilterChange(filter: string): void {
    if (this.selectedFilter === filter && !this.chartLoading) return;
    this.selectedFilter = filter;
    this.activeHoverItem = null;
    this.loadChartData();
  }

  /**
   * Calculate bar height percentage with safe minimum for visibility
   */
  getBarHeight(count: number): number {
    if (this.maxCount <= 0 || !count) return 4;
    const rawPct = (count / this.maxCount) * 100;
    return Math.max(rawPct, 6); // Keep minimum 6% height so even small counts are visible and interactable
  }

  /**
   * Hover handling for chart item tooltip
   */
  onHoverItem(item: ChartItem | null): void {
    this.activeHoverItem = item;
  }

  /**
   * Export / download user report
   */
  downloadReport(): void {
    if (this.downloading) return;
    this.downloading = true;

    this.dashboardService.downloadUserReport().subscribe({
      next: (response: any) => {
        const blob = response instanceof Blob ? response : new Blob([response]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const timestamp = new Date().toISOString().split('T')[0];
        const filename = `boxing-reflex-user-report-${timestamp}.xlsx`;
        
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        this.downloading = false;
        this.toastService.successToast('Report downloaded successfully');
      },
      error: (error: any) => {
  console.error('Error downloading report:', error);
  this.downloading = false;
}
    });
  }
}
